import * as React from 'react'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { Button, Checkbox, Chip, Icon, IconButton, IModalProps, SearchBox, SkeletonLoader, SlideInPanel, useAlert, useDebounce, useEventSubscriber, useToast } from 'uxp/components'
import { IContextProvider } from '../../uxp'
import { filterTemplates, getCardTemplates, IWidgetTemplate, WEB_STUB_TEMPLATE_IDS } from '../common/common'
import { hasValue, loadScriptFileAsync, tryParseJSON } from '../../util'
import classNames from 'classnames'
import './TemplateSelector.scss'

interface ITemplateSelectorProps extends IModalProps {
    uxpContext: IContextProvider,
    onSelectTemplate: (id: string, moduleId?: string) => void,
    expectedSchema?: any,
    exactSchemaMatch?: boolean,
}

const TemplateSelector: React.FunctionComponent<ITemplateSelectorProps> = (props) => {
    const { show, onClose, onOpen, uxpContext, onSelectTemplate } = props
    const [selected, setSelected] = React.useState(null)
    const [targetSchema, setTargetSchema] = React.useState<any>(props.expectedSchema)
    const [showAll, setShowAll] = React.useState(false)

    const [query, setQuery] = React.useState('')
    const [view, setView] = React.useState<'default' | 'custom' | 'user'>('default')
    const [allUserTemplates, setAllUserTemplates] = React.useState([])
    const [allCustomTemplates, setAllCustomTemplates] = React.useState([])
    const [loadingTemplates, setLoadingTemplates] = React.useState(true)

    const toast = useToast()
    const alerts = useAlert()
    const debouncedQuery = useDebounce(query, 300)

    useEventSubscriber('template-selector', 'save-as-template', (data) => {
        getAllUserTemplates()
    })

    // Preserve the v4 Modal contract: onOpen fired when the selector becomes visible.
    React.useEffect(() => {
        if (show) onOpen?.()
    }, [show])

    React.useEffect(() => {
        getAllUserTemplates()
    }, [])
    React.useEffect(() => {
        setSelected(null)
    }, [props])
    React.useEffect(() => {
        if (showAll) setTargetSchema(null)
        else setTargetSchema(props.expectedSchema)

    }, [props.expectedSchema, showAll])

    React.useEffect(() => {
        getAllUserTemplates()
    }, [view])

    function renderIcon(icon: IconProp | JSX.Element) {
        if (React.isValidElement(icon)) return icon
        return <Icon icon={icon as IconProp} />
    }


    async function getAllUserTemplates() {

        setLoadingTemplates(true)
        const defaultTemplates = getCardTemplates()
        const customTemplates = await loadCustomWidgetTemplates()

        const allTemplates = defaultTemplates.concat(customTemplates)

        uxpContext.executeService('UXP', 'UserWidgetTemplate:AllTemplates', {}, { json: true })
            .then(res => {
                const processed = res.map((r: any) => {
                    const { Name, Description, Template, Key, WidgetID } = r
                    const _template = tryParseJSON(Template)

                    if (!_template) return null

                    // find default template
                    const defInst = allTemplates.find(t => t.id == _template?.__template__?.type)
                    if (!defInst) return null

                    const parts = WidgetID.split('/')
                    let moduleId = null
                    if (hasValue(parts[0]) && !parts[0].startsWith('uxp-widget-designer-')) moduleId = parts[0].trim()

                    return {
                        id: `user-template@@@${defInst.id}@@@${Key}`,
                        name: Name,
                        description: Description || defInst.description || '',
                        icon: defInst.icon || null,
                        expectedSchema: defInst.expectedSchema || null,
                        template: defInst.template,
                        defaultProps: _template.uiProps,
                        complexity: defInst.complexity,
                        key: Key,
                        moduleId: moduleId
                    } as IWidgetTemplate
                }).filter((r: any) => !!r)
                setAllUserTemplates(processed)
            })
            .catch(e => {
                console.log('unable to get all the user templates. ', e)
            })
            .finally(() => setLoadingTemplates(false))
    }


    function getUserTemplates(expectedSchema?: any, exactMatch: boolean = false, query?: string) {
        const filtered = filterTemplates(allUserTemplates, expectedSchema, exactMatch, query)
        return filtered
    }

    async function getAllCustomTemplates() {
        try {
            const templates = await uxpContext.executeService('UXP', 'Widget:GetAllTemplates', {}, { json: true })
            return templates
        } catch (e) {
            console.log('unable to get the custom widget templates. Exception: ', e);
            return []
        }
    }

    async function loadCustomWidgetTemplates() {
        const templates = await getAllCustomTemplates()
        // Remember each template's origin module id so a widget saved from it points at the real
        // source bundle — not the designer's own bundle id (which 400s: it's a global external).
        const moduleByTemplateId: { [id: string]: string } = {}
        for (const t of templates) {
            const parts = t.ID.split('/')
            const moduleId = parts[0]
            const templateId = parts[parts.length - 1]   // last segment = the widget/template id

            if (!hasValue(moduleId) || !hasValue(templateId)) continue;

            // load script file
            if (moduleId == 'uxp-widget-designer-bundle') continue
            moduleByTemplateId[String(templateId).trim().toLowerCase()] = String(moduleId).trim()
            await loadScriptFileAsync(`/api/UXP/module?id=${moduleId}`, moduleId)
        }

        const registeredCustomTemplates = getCardTemplates(null, null, null, true).map((t: IWidgetTemplate) =>
            t.moduleId ? t : { ...t, moduleId: moduleByTemplateId[String(t.id).toLowerCase()] }
        )
        setAllCustomTemplates(registeredCustomTemplates)
        return registeredCustomTemplates
    }

    function getCustomTemplates(expectedSchema?: any, exactMatch: boolean = false, query?: string) {
        const filtered = filterTemplates(allCustomTemplates, expectedSchema, exactMatch, query)
        return filtered
    }


    async function deleteTemplate(key: string) {
        const confirm = await alerts.confirm(uxpContext.$L('widgetdesigner.templates.delete-confirm'))

        if (confirm) {
            uxpContext.executeService('UXP', 'UserWidgetTemplate:Delete', { Key: key })
                .then(res => {
                    toast.success(uxpContext.$L('widgetdesigner.messages.template-deleted'))
                    getAllUserTemplates()
                })
                .catch(e => {
                    toast.error(uxpContext.$L('widgetdesigner.messages.template-delete-failed'))
                    console.log('error deleting template ', e);
                })
        }

        return
    }

    // ---- current view → template list (data layer unchanged) ----
    function currentTemplates(): IWidgetTemplate[] {
        if (view == 'default')
            return getCardTemplates(targetSchema, props.exactSchemaMatch, debouncedQuery)
                .filter((t) => !WEB_STUB_TEMPLATE_IDS.includes(t.id))
        if (view == 'user') return getUserTemplates(targetSchema, props.exactSchemaMatch, debouncedQuery)
        return getCustomTemplates(targetSchema, props.exactSchemaMatch, debouncedQuery)
    }

    // ---- left sidebar: the three template views (v5 nav language) ----
    function renderSidebar() {
        const items: { view: 'default' | 'custom' | 'user'; icon: IconProp; label: string }[] = [
            { view: 'default', icon: ['fal', 'layer-group'] as IconProp, label: uxpContext.$L('widgetdesigner.templates.default') },
            { view: 'custom', icon: ['fal', 'puzzle-piece'] as IconProp, label: uxpContext.$L('widgetdesigner.templates.custom') },
            { view: 'user', icon: ['fal', 'user'] as IconProp, label: uxpContext.$L('widgetdesigner.templates.mine') },
        ]
        return <div className="uxp_wd-dsp__sidebar">
            <div className="uxp_wd-dsp__sidebar-label">{uxpContext.$L('widgetdesigner.templates.sidebar-label')}</div>
            {items.map(i => (
                <div
                    key={i.view}
                    className={'uxp_wd-dsp__sidebar-item' + (view === i.view ? ' uxp_wd-dsp__sidebar-item--selected' : '')}
                    onClick={() => { setView(i.view); setSelected(null) }}
                >
                    <Icon className="uxp_wd-dsp__sidebar-icon" icon={i.icon} />
                    <span className="uxp_wd-dsp__sidebar-text">{i.label}</span>
                </div>
            ))}
        </div>
    }

    // ---- toolbar: full-width search + (schema-gated) Show All Templates ----
    function renderToolbar() {
        return <div className="uxp_wd-dsp__toolbar">
            <div className="uxp_wd-dsp__search">
                <SearchBox value={query} onChange={setQuery} />
            </div>
            {!!props.expectedSchema &&
                <Checkbox
                    checked={showAll}
                    onChange={setShowAll}
                    label={uxpContext.$L('widgetdesigner.templates.show-all')}
                />
            }
        </div>
    }

    // ---- template card: icon + name + 2-line description; user templates get a
    // trash action (stopPropagation so it never selects the card). ----
    function renderCard(template: IWidgetTemplate, canDelete?: boolean) {
        const isSelected = template.id == selected?.id
        return <div
            key={template.id}
            className={'uxp_wd-dsp__card' + (isSelected ? ' uxp_wd-dsp__card--selected' : '')}
            role="button"
            tabIndex={0}
            onClick={() => setSelected(template)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setSelected(template)
                }
            }}
        >
            <div className="uxp_wd-tsel__card-inner">
                <div className="uxp_wd-tsel__card-icon">{renderIcon(template.icon)}</div>
                <div className="uxp_wd-tsel__card-body">
                    <div className="uxp_wd-tsel__card-name">{template.name}</div>
                    <div className="uxp_wd-tsel__card-desc">{template.description}</div>
                </div>
                {canDelete &&
                    <span
                        className="uxp_wd-tsel__card-delete"
                        onClick={(e) => { e.stopPropagation(); deleteTemplate(template.key) }}
                    >
                        <IconButton icon="fal trash" borderless onClick={() => { }} />
                    </span>
                }
            </div>
        </div>
    }

    function renderCards(rail?: boolean) {
        const list = currentTemplates()
        if (loadingTemplates && !list.length) {
            // Card-grid skeleton while the template lists are being fetched.
            return <div className={classNames('uxp_wd-dsp__cards', { 'uxp_wd-dsp__cards--rail': rail })}>
                {Array(6).fill(0).map((_, i) => (
                    <SkeletonLoader key={i} className="uxp_wd-dsp__card-skeleton" height="5.5rem" />
                ))}
            </div>
        }
        if (!list.length) return <div className="uxp_wd-dsp__empty">{uxpContext.$L('widgetdesigner.templates.none-found')}</div>
        const canDelete = view == 'user'
        return <div className={classNames('uxp_wd-dsp__cards', { 'uxp_wd-dsp__cards--rail': rail })}>
            {list.map(t => renderCard(t, canDelete))}
        </div>
    }

    // ---- master-detail: rail (search + stacked cards) + detail (live preview) ----
    function renderDetail() {
        if (!selected) return null
        return <div className="uxp_wd-dsp__detail-inner">
            <div className="uxp_wd-dsp__detail-titlerow">
                <span className="uxp_wd-dsp__detail-back">
                    <IconButton icon="fal chevron-left" borderless onClick={() => setSelected(null)} />
                </span>
                <div className="uxp_wd-dsp__detail-title">{selected.name}</div>
                {selected.complexity &&
                    <Chip
                        label={selected.complexity}
                        size="small"
                        variant={({ easy: 'success', medium: 'info', hard: 'danger', advanced: 'warning' } as any)[selected.complexity] || 'secondary'}
                    />}
            </div>

            {selected.description &&
                <p className="uxp_wd-tsel__detail-desc">{selected.description}</p>}

            <div className="uxp_wd-tsel__preview">
                <div className="uxp_wd-tsel__preview-box" style={{ width: 300, height: 300 }}>
                    <div className={'wd-work-area-widget-container'}>
                        {React.createElement(selected.template, { designer: { mode: 'thumbnail' }, uiProps: selected.defaultProps || {}, uxpContext: uxpContext })}
                    </div>
                </div>
            </div>
        </div>
    }

    function renderPane() {
        if (selected) {
            return <div className="uxp_wd-dsp__step">
                <div className="uxp_wd-dsp__split">
                    <div className="uxp_wd-dsp__rail">
                        {renderToolbar()}
                        <div className="uxp_wd-dsp__rail-list">
                            {renderCards(true)}
                        </div>
                    </div>
                    <div className="uxp_wd-dsp__detail">{renderDetail()}</div>
                </div>
            </div>
        }
        return <div className="uxp_wd-dsp__step">
            {renderToolbar()}
            <div className="uxp_wd-dsp__grid">
                {renderCards()}
            </div>
        </div>
    }

    return <SlideInPanel isOpen={show} onClose={onClose} mode="full">
        <div className="uxp_wd-dsp uxp_wd-tsel">
            <div className="uxp_wd-dsp__topbar">
                <div className="uxp_wd-dsp__title">{uxpContext.$L('widgetdesigner.templates.select')}</div>
                <div className="uxp_wd-dsp__topbar-actions">
                    {selected &&
                        <Button
                            title={uxpContext.$L('widgetdesigner.templates.edit')}
                            variant="primary"
                            size="small"
                            onClick={() => onSelectTemplate(selected.id, selected?.moduleId)}
                        />}
                    <span className="uxp_wd-dsp__close">
                        <IconButton type="close" variant="secondary" size="small" onClick={() => onClose?.()} />
                    </span>
                </div>
            </div>
            <div className="uxp_wd-dsp__layout">
                {renderSidebar()}
                <div className="uxp_wd-dsp__pane">
                    {renderPane()}
                </div>
            </div>
        </div>
    </SlideInPanel>
}

export default TemplateSelector
